import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, MapPin, Phone, Mail } from "lucide-react";
import { BRAND, NAV_LINKS } from "../data/content";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-[#2A1E17] text-[#FAF7F2] mt-20 overflow-hidden">
      <img
        src={BRAND.milletGraphic}
        alt=""
        aria-hidden="true"
        className="absolute -right-20 top-0 w-96 opacity-10 rotate-[20deg] pointer-events-none select-none"
      />
      <img
        src={BRAND.milletGraphic}
        alt=""
        aria-hidden="true"
        className="absolute -left-20 bottom-10 w-72 opacity-10 rotate-[-160deg] pointer-events-none select-none"
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <img src={BRAND.logoWhite} alt="Stories — The Millet Café" className="h-32 w-32 sm:h-36 sm:w-36 object-contain -ml-2" />
          <p className="font-serif-display text-2xl italic mt-8 leading-snug">
            “Come for the food. Stay for the conversation.”
          </p>
          <p className="text-sm text-[#FAF7F2]/70 mt-6 max-w-md">
            India&apos;s first conversation-first millet café. A cozy, all-day space where food,
            conversations and community create daily repeat.
          </p>
          <p className="text-xs tracking-[0.22em] uppercase text-[#D4A373] mt-8">
            {BRAND.backedBy}
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs tracking-[0.22em] uppercase text-[#D4A373] mb-5">Explore</div>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  className="text-[#FAF7F2]/80 hover:text-[#D4A373] text-sm link-underline"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-xs tracking-[0.22em] uppercase text-[#D4A373] mb-5">Visit Us</div>
          <ul className="space-y-4 text-sm text-[#FAF7F2]/80">
            <li className="flex gap-3">
              <MapPin size={18} className="text-[#D4A373] flex-none mt-1" />
              <span>{BRAND.contact.address}</span>
            </li>
            <li className="flex gap-3">
              <Phone size={18} className="text-[#D4A373] flex-none mt-1" />
              <a href={`tel:${BRAND.contact.phone}`} data-testid="footer-phone">
                {BRAND.contact.phone}
              </a>
            </li>
            <li className="flex gap-3">
              <Mail size={18} className="text-[#D4A373] flex-none mt-1" />
              <a href={`mailto:${BRAND.contact.email}`} data-testid="footer-email">
                {BRAND.contact.email}
              </a>
            </li>
          </ul>

          <div className="flex gap-3 mt-8">
            <a
              href={BRAND.social.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram"
              className="h-10 w-10 rounded-full border border-[#D4A373]/40 flex items-center justify-center hover:bg-[#D4A373]/15 transition"
            >
              <Instagram size={16} />
            </a>
            <a
              href={BRAND.social.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-facebook"
              className="h-10 w-10 rounded-full border border-[#D4A373]/40 flex items-center justify-center hover:bg-[#D4A373]/15 transition"
            >
              <Facebook size={16} />
            </a>
            <a
              href={BRAND.social.twitter}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-twitter"
              className="h-10 w-10 rounded-full border border-[#D4A373]/40 flex items-center justify-center hover:bg-[#D4A373]/15 transition"
            >
              <Twitter size={16} />
            </a>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
          {/* Header band */}
          <div className="flex flex-col items-center text-center mb-8">
            <span className="text-[10px] tracking-[0.32em] uppercase text-[#D4A373]">
              Our Commitment
            </span>
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_millet-stories/artifacts/9ryccg81_sdg%20logo.png"
                alt="UN Sustainable Development Goals"
                data-testid="footer-sdg-logo"
                className="h-9 sm:h-10 w-auto object-contain bg-white rounded-md px-2 py-1 flex-none"
              />
              <p className="text-sm sm:text-base text-[#FAF7F2]/90 leading-snug max-w-2xl">
                Proudly aligned with the United Nations{" "}
                <span className="text-[#D4A373] font-medium">Sustainable Development Goals</span>.
              </p>
            </div>
          </div>

          {/* Goal cards */}
          <ul
            className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto"
            data-testid="footer-sdg-goals"
          >
            {[
              {
                num: 3,
                title: "Good Health & Well-Being",
                blurb: "Millet-led, nourishing food for everyday wellness.",
                img: "https://customer-assets.emergentagent.com/job_millet-stories/artifacts/phns3dyw_goal3.JPG",
                accent: "#4C9F38",
              },
              {
                num: 8,
                title: "Decent Work & Economic Growth",
                blurb: "Fair sourcing, dignified café jobs and farmer partnerships.",
                img: "https://customer-assets.emergentagent.com/job_millet-stories/artifacts/lq6t5dgy_goal8.JPG",
                accent: "#A21942",
              },
              {
                num: 11,
                title: "Sustainable Cities & Communities",
                blurb: "Cafés that build community, mindfully and locally.",
                img: "https://customer-assets.emergentagent.com/job_millet-stories/artifacts/mudzeee6_goal11.JPG",
                accent: "#FD9D24",
              },
            ].map((g) => (
              <li
                key={g.num}
                data-testid={`footer-sdg-${g.num}`}
                className="group bg-white/[0.05] hover:bg-white/[0.09] border border-white/10 rounded-2xl p-4 flex items-start gap-3 transition-colors"
              >
                <img
                  src={g.img}
                  alt={`SDG ${g.num} — ${g.title}`}
                  className="h-14 w-14 object-cover rounded-md flex-none"
                />
                <div className="min-w-0 flex-1">
                  <div
                    className="text-[10px] tracking-[0.22em] uppercase font-semibold"
                    style={{ color: g.accent }}
                  >
                    Goal {g.num}
                  </div>
                  <div className="text-sm font-medium text-[#FAF7F2] mt-0.5 leading-tight">
                    {g.title}
                  </div>
                  <p className="text-[12px] text-[#FAF7F2]/65 mt-1.5 leading-snug">
                    {g.blurb}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#FAF7F2]/60">
          <span data-testid="footer-copy" className="text-center md:text-left">
            © {new Date().getFullYear()} Stories — The Millet Café. All rights reserved.
          </span>
          <span className="font-script text-base text-[#D4A373] text-center">
            Real Food · Real Conversations · Real Impact
          </span>
          <span className="text-center md:text-right">
            Designed &amp; Developed by{" "}
            <a
              href="https://www.reachvel.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit-reachvel"
              className="text-[#D4A373] hover:text-[#FAF7F2] transition-colors link-underline font-medium"
            >
              Reachvel
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
